import {
  clearConstructionPlan,
  elementalsSetPlanTokens,
  removeConstructionPlanEntries,
  removeConstructionPlanEntriesAt,
  restoreConstructionPlan,
  setConstructionPlanAutoCompleteTurn,
  setConstructionPlanInvalidHexKeys,
  setSelectedToken,
  showToast
} from '../actions';
import {selectConstructionPlanEntries, selectConstructionPlanState} from '../reducers/privateStateReducer/selectors';
import {
  selectCurrentPlayer,
  selectElementalsPlanTokensByPlayerId,
  selectPendingBuildings,
  selectPhase,
  selectRound
} from '../reducers/sharedStateReducer/selectors';
import {
  CONSTRUCTION_PLAN_CONTEXTS,
  getConstructionPlanContext,
  getConstructionPlanPlayer,
  getConstructionPlanTurnKey
} from '../../utils/gameState/construction/planning/getConstructionPlanContext';
import {
  applyConstructionPlan,
  CONSTRUCTION_PLAN_APPLY_STATUSES,
  getDueConstructionPlanTurnKey
} from '../../utils/gameState/construction/planning/applyConstructionPlan';
import {collectUnreachablePlanEntries} from '../../utils/gameState/construction/planning/collectUnreachablePlanEntries';
import {validateConstructionPlan} from '../../utils/gameState/construction/planning/validateConstructionPlan';
import {validateCurrentPlayerPendingBuildings} from '../../utils/gameState/construction/pendingBuildingsValidation';
import {handleCompleteTurn} from '../../utils/gameState/turn/handleCompleteTurn';
import {checkIfTurnInputBlocked} from '../../utils/gameState/turn/turnInputLock';
import {TEXT_AUTO_COMPLETE_TURN_DISABLED, TEXT_AUTO_COMPLETE_TURN_NOT_DONE} from '../../constants/text/construction';
import {COMMON_BUILDING_TOKENS} from '../../constants/tokens';
import {QUARTERS_OF_DIFFERENT_CITIES_MIN_HEXES_BETWEEN} from '../../constants/construction';
import {checkIfAnyQuarter} from '../../utils/buildings/checkIfAnyQuarter';
import {checkIfSpecialBuilding} from '../../utils/buildings/checkIfSpecialBuilding';
import {HexMath} from '../../utils/HexMath';
import {ConstructionAccessibility} from '../../utils/ConstructionAccessibility';
import {isPlayerFaction} from '../../utils/factions/isPlayerFaction';
import {FACTIONS} from '../../constants/factions';
import {addMoreElementalsConstructionTokens} from '../../utils/factions/elementals/addMoreElementalsConstructionTokens';

// Тост о применении плана — после оверлея «Ваш ход» (появляется через 100 мс
// и держится около 1,5 секунды, см. EffectsAutoPlayer/TurnOverlay), иначе его
// не прочитать
const APPLY_MESSAGE_DELAY_MS = 1650;
const APPLY_MESSAGE_DURATION_MS = 8000;
// Повторный запрос состояния — не раньше, чем сервер снова ответит на
// REQUEST_STATE этого сокета (STATE_RESYNC_COOLDOWN_MS = 3000 в
// server/index.js): снимок, затерший план, мог быть ответом на такой же запрос
const RESYNC_DELAY_MS = 3500;

// Строительные действия самого игрока после применения плана: после них план
// повторно не применяется (игрок уже распорядился своим ходом)
const LOCAL_CONSTRUCTION_ACTION_TYPES = ['SET_HEX_TOKEN', 'SET_SPECIAL_BUILDING', 'UNDO_PENDING_BUILDING', 'REPAIR_BUILDING'];

// Рамка плана: другое лобби, раунд или фаза — план прошлой рамки не нужен.
// Сравнение до/после каждого экшена ловит и скачок через LOAD_STATE
// (переподключение после долгого сна вкладки)
const getPlanScopeKey = (state) => {
  const gameState = state?.shared?.gameState;
  return `${state?.private?.lobby?.lobbyId}|${gameState?.round}|${gameState?.phase}`;
};

/**
 * Ячейки плана, на которых этим экшеном появилось чужое строение. Любой
 * источник: ретранслированный ход игрока, серверное действие, ход бота,
 * исполняемый этим клиентом, LOAD_STATE. Отклоненная редьюсером стройка поле
 * не меняет — и план не трогает.
 */
export const collectCancelledPlanHexKeys = (prevState, nextState, entries) => {
  const prevHexes = prevState?.shared?.hexes || {};
  const nextHexes = nextState?.shared?.hexes || {};
  const prevSpecialBuildings = prevState?.shared?.gameState?.specialBuildings || {};
  const nextSpecialBuildings = nextState?.shared?.gameState?.specialBuildings || {};
  if (prevHexes === nextHexes && prevSpecialBuildings === nextSpecialBuildings) {
    return [];
  }

  const cancelled = new Set();
  entries.forEach(({hexKey}) => {
    const isTokenBuilt = Boolean(nextHexes[hexKey]) && nextHexes[hexKey] !== prevHexes[hexKey];
    const isSpecialBuildingBuilt = Boolean(nextSpecialBuildings[hexKey])
      && nextSpecialBuildings[hexKey] !== prevSpecialBuildings[hexKey];
    if (isTokenBuilt || isSpecialBuildingBuilt) {
      cancelled.add(hexKey);
    }
  });
  return [...cancelled];
};

// Кварталы и спецстроения снимаются в том же радиусе, на котором кварталы
// разных городов стоять не могут (зона кварталов): спецстроение стоит на
// квартале и разделяет его судьбу
export const PLAN_CANCEL_RADIUS_FOR_QUARTERS_AND_SPECIAL_BUILDINGS = QUARTERS_OF_DIFFERENT_CITIES_MIN_HEXES_BETWEEN;
// Все остальное (ферма, казарма и любые типы, явно не перечисленные в
// getPlanCancelRadiusAroundNewQuarter) — только вплотную к новому кварталу
export const PLAN_CANCEL_RADIUS_FOR_OTHER_BUILDINGS = 1;

/**
 * В каком радиусе от нового квартала соперника снимается запланированное
 * строение token. null — не снимается никогда.
 *  - дорога — не снимается: новый квартал рядом ей не мешает;
 *  - базовый и центральный квартал, любое спецстроение — в радиусе
 *    PLAN_CANCEL_RADIUS_FOR_QUARTERS_AND_SPECIAL_BUILDINGS. Спецстроения
 *    проверяются общим признаком, поэтому новые типы попадают сюда сами;
 *  - все прочее — в радиусе PLAN_CANCEL_RADIUS_FOR_OTHER_BUILDINGS.
 */
export const getPlanCancelRadiusAroundNewQuarter = (token) => {
  if (token === COMMON_BUILDING_TOKENS.ROAD) {
    return null;
  }
  if (checkIfAnyQuarter(token) || checkIfSpecialBuilding(token)) {
    return PLAN_CANCEL_RADIUS_FOR_QUARTERS_AND_SPECIAL_BUILDINGS;
  }
  return PLAN_CANCEL_RADIUS_FOR_OTHER_BUILDINGS;
};

// Радиус меряется так же, как зона кварталов в самой игре
// (calculateQuarterZoneCells): обходом соседних ячеек, который не проходит
// сквозь горы. За горой запланированное остается, если игра и сама позволила
// бы там строить
const constructionAccessibility = new ConstructionAccessibility(new HexMath(30, 'flat'));

/**
 * Записи плана, которые снимает квартал (базовый или центральный), появившийся
 * на поле этим экшеном: у каждого типа строения свой радиус, см.
 * getPlanCancelRadiusAroundNewQuarter. Снимаются именно записи, а не ячейки:
 * на одной ячейке запланированный квартал может стоять поверх
 * запланированной дороги — квартал уйдет, дорога останется. Источник, как и у
 * collectCancelledPlanHexKeys, любой: ретранслированный ход, ход бота,
 * исполняемый этим клиентом, LOAD_STATE.
 */
export const collectPlanEntriesNearNewQuarters = (prevState, nextState, entries) => {
  const prevHexes = prevState?.shared?.hexes || {};
  const nextHexes = nextState?.shared?.hexes || {};
  if (prevHexes === nextHexes || entries.length === 0) {
    return [];
  }

  const newQuarterHexKeys = Object.keys(nextHexes).filter((hexKey) => (
    nextHexes[hexKey] !== prevHexes[hexKey] && checkIfAnyQuarter(nextHexes[hexKey])
  ));
  if (newQuarterHexKeys.length === 0) {
    return [];
  }

  // Ячейки в пределах радиуса от любого нового квартала — по одному обходу на радиус
  const cellsWithinRadius = new Map();
  const getCellsWithinRadius = (radius) => {
    if (!cellsWithinRadius.has(radius)) {
      const cells = new Set();
      newQuarterHexKeys.forEach((quarterHexKey) => {
        constructionAccessibility.findReachableCells(quarterHexKey, radius, nextHexes)
          .forEach((cellKey) => cells.add(cellKey));
      });
      cellsWithinRadius.set(radius, cells);
    }
    return cellsWithinRadius.get(radius);
  };

  return entries.filter(({hexKey, token}) => {
    const radius = getPlanCancelRadiusAroundNewQuarter(token);
    return radius !== null && getCellsWithinRadius(radius).has(hexKey);
  });
};

const NEWLINE = String.fromCharCode(10);

const checkIfMapChanged = (prevMap = {}, nextMap = {}) => {
  if (prevMap === nextMap) {
    return false;
  }
  const nextKeys = Object.keys(nextMap);
  return Object.keys(prevMap).length !== nextKeys.length || nextKeys.some((key) => prevMap[key] !== nextMap[key]);
};

// Поменялось ли поле — жетоны или спецстроения — по содержимому, а не по
// ссылке: LOAD_STATE и переподключение приносят новые объекты с тем же полем,
// и перепроверять план (и сообщать о снятии) там не из-за чего
const checkIfFieldChanged = (prevState, nextState) => (
  checkIfMapChanged(prevState?.shared?.hexes, nextState?.shared?.hexes)
  || checkIfMapChanged(prevState?.shared?.gameState?.specialBuildings, nextState?.shared?.gameState?.specialBuildings)
);

// Текст о снятии плана: главная причина — стройка на ячейке или квартал
// поблизости, а недостижимое — отдельной строкой. Если снято только
// недостижимое — оно и есть главная причина. Строящего называют виновником,
// только если путь отрезал именно он (isCutOff): до недостижимого, которое
// игрок оставил в плане сам, пути не было и раньше
const buildPlanCancelReasonMessage = ({builderName, isBuiltOn, isNearQuarter, isUnreachable, isCutOff}) => {
  let message = null;
  if (isNearQuarter) {
    message = `${builderName} построил квартал поблизости — запланированные строения рядом с ним сняты с плана`;
  } else if (isBuiltOn) {
    message = `${builderName} построил на запланированной ячейке — строение снято с плана`;
  }
  if (!isUnreachable) {
    return message;
  }
  if (!isCutOff) {
    return message
      ? `${message}${NEWLINE}До части запланированного не провести путь строительства — оно тоже снято`
      : 'До части запланированного не провести путь строительства — оно снято с плана';
  }
  return message
    ? `${message}${NEWLINE}До части запланированного больше не провести путь строительства — оно тоже снято`
    : `${builderName} отрезал путь строительства — запланированное, до которого его больше не провести, снято с плана`;
};

// isAutoCompleteTurnDisabled — снятие сняло и чекбокс автоматического
// завершения хода: план уже не тот, что игрок проверил и доверил ходу
export const buildPlanCancelledMessage = ({isAutoCompleteTurnDisabled = false, ...reasons}) => {
  const message = buildPlanCancelReasonMessage(reasons);
  return isAutoCompleteTurnDisabled && message
    ? `${message}${NEWLINE}${NEWLINE}${TEXT_AUTO_COMPLETE_TURN_DISABLED}`
    : message;
};

// Кто построил на запланированной ячейке: ходящий игрок, если экшен ход не
// передавал. LOAD_STATE после переподключения мог принести ходы нескольких
// игроков — тогда без имени
const getBuilderName = (prevState, nextState) => {
  const builder = selectCurrentPlayer(prevState);
  return builder && builder.id === selectCurrentPlayer(nextState)?.id ? builder.name : null;
};

/**
 * План строительства в сетевой игре (только клиент: у серверного стора
 * middleware нет). На каждом экшене:
 *  1. смена лобби, раунда или фазы — план снимается;
 *  2. планировать больше нечего (свой ход строительства в этой фазе завершен,
 *     партия окончена, игрок стал зрителем) — план снимается;
 *  3. чужая стройка на запланированной ячейке снимает запланированное на ней,
 *     а чужой новый квартал (базовый или центральный) — запланированное рядом
 *     с собой, в радиусе своего типа (getPlanCancelRadiusAroundNewQuarter), а после
 *     любой чужой стройки — и то, до чего больше не провести путь строительства
 *     (collectUnreachablePlanEntries). Все это —
 *     и когда тот же экшен передал ход этому игроку (LOAD_STATE после
 *     переподключения приносит сразу и чужие ходы, и начало своего);
 *  4. выбрано автоматическое завершение хода (чекбокс) — после любой чужой
 *     стройки и после правки плана самим игроком план проверяется заново
 *     (validateConstructionPlan): чекбокс держится только у валидного плана.
 *     Снятие запланированного (п. 3) снимает и чекбокс;
 *  5. наступил собственный ход строительства — план применяется
 *     (applyConstructionPlan) вне текущего диспатча: сначала доигрывают
 *     эффекты экшена, начавшего ход, а слепок хода в отправляемых действиях
 *     уже показывает игрока текущим. С чекбоксом ход тут же завершается, если
 *     план встал целиком и строения хода прошли проверку; иначе ход остается
 *     игроку с подсветкой невалидного и сообщением.
 * Элементаль, попав в планирование, сразу тянет жетоны элементов на свой ход
 * (ensureElementalsPlanTokens) — даже с пустым планом: по ним планирование
 * проверяет, хватит ли элементов запланированному.
 * Без соединения применение откладывается до свежего LOAD_STATE после
 * переподключения. LOAD_STATE после применения, в котором примененного нет,
 * разбирается в handleLoadStateAfterApply.
 *
 * Зависимости от окружения передаются снаружи (store.js, e2e-харнессы):
 *  - isSocketOpen() — открыто ли соединение матча;
 *  - getSocket() — текущий объект соединения (при переподключении — новый):
 *    по нему LOAD_STATE нового соединения отличается от ответа по прежнему.
 *    Без него план повторно не применяется никогда;
 *  - requestState() — запросить у сервера полное состояние (REQUEST_STATE);
 *  - playPlanCancelledSound() — звук отмены строительства: запланированное
 *    сняла чужая стройка (на той же ячейке или квартал поблизости);
 *  - playTurnStartSound() — звук начала хода (тот же, что у оверлея «Ваш
 *    ход»): при автоматическом завершении хода оверлея нет, а звук
 *    подтверждает игроку, что его план построен;
 *  - completeTurn(store) — завершить ход игрока, как кнопка завершения фазы.
 *
 * Своего звука у применения плана нет намеренно: начало хода и так озвучено
 * (оверлей «Ваш ход» с молотками), а остальные участники матча слышат обычный
 * звук стройки на пришедших к ним действиях (см. playEffectForServerAction).
 */
export const createConstructionPlanMiddleware = ({
  isSocketOpen = () => true,
  getSocket = () => null,
  requestState = () => {},
  playPlanCancelledSound = () => {},
  playTurnStartSound = () => {},
  // Без звука завершения хода: ход завершает не клик игрока, а звучит в этот
  // момент начало хода (playTurnStartSound)
  completeTurn = (targetStore) => handleCompleteTurn(targetStore, true, false, null),
  applyMessageDelayMs = APPLY_MESSAGE_DELAY_MS,
  resyncDelayMs = RESYNC_DELAY_MS,
  checkIfTurnAutoCompleted,
} = {}) => (store) => {
  let applyTimerId = null;
  // Последнее примененное: {turnKey, entries, isAutoCompleteTurn, socket,
  // isTouched, isRetried, isResyncRequested, scopeKey, completedScopeKey}
  let lastApply = null;
  // Идет автоматическое завершение хода: рамку плана меняет оно само
  let isCompletingTurn = false;
  // Применение отложено без соединения: после переподключения план ждет
  // свежего LOAD_STATE. Иначе (например, по LOBBY_UPDATE, который сервер шлет
  // перед ним) действия ушли бы по новому соединению раньше, чем придет
  // LOAD_STATE, собранный сервером до них, — и он стер бы их у себя
  let isAwaitingFreshState = false;
  // Жетоны элементов, вытянутые этим клиентом при планировании:
  // {scopeKey, playerId, tokens}
  let elementalsPlanDraw = null;
  let isDrawingElementalsPlanTokens = false;
  // Бросок отложен без соединения: после переподключения он ждет свежего
  // LOAD_STATE, как и применение плана (см. isAwaitingFreshState)
  let isElementalsPlanDrawAwaitingFreshState = false;

  /**
   * Элементаль в планировании тянет жетоны элементов сразу, а не в начале
   * хода: без них не проверить, хватит ли элементов запланированному. Бросок
   * уходит в общее состояние (ELEMENTALS_SET_PLAN_TOKENS) и с началом хода
   * становится жетонами хода.
   *
   * Бросок окончательный. Сервер повторный не принимает, а здесь новый не
   * тянется: если LOAD_STATE пришел без броска (снимок собран раньше, чем
   * сервер его получил, или бросок потерялся с прежним соединением), уходит
   * тот же бросок. Повтор — только по LOAD_STATE: иначе отвергнутый по
   * какой-то причине бросок отправлялся бы на каждом экшене. LOAD_STATE другой
   * рамки (раунд или фаза) бросок прошлой забывает: партия назад не ходит, и
   * если свежее состояние вернуло игрока в более раннюю фазу, сервер броска не
   * получил — в своей рамке он тянется заново.
   *
   * Без соединения бросок ждет его, а после переподключения — свежего
   * LOAD_STATE: сервер шлет LOBBY_UPDATE раньше него, и бросок, ушедший по
   * локальному (возможно, уже откаченному) раунду и фазе, сервер отверг бы как
   * устаревший
   */
  const ensureElementalsPlanTokens = (state, action) => {
    if (action.type === 'LOAD_STATE') {
      isElementalsPlanDrawAwaitingFreshState = false;
      if (elementalsPlanDraw && elementalsPlanDraw.scopeKey !== getPlanScopeKey(state)) {
        elementalsPlanDraw = null;
      }
    }
    if (isDrawingElementalsPlanTokens
      || getConstructionPlanContext(state) !== CONSTRUCTION_PLAN_CONTEXTS.PLANNING
    ) {
      return;
    }
    const player = getConstructionPlanPlayer(state);
    if (!isPlayerFaction(player, FACTIONS.ELEMENTALS) || selectElementalsPlanTokensByPlayerId(state)[player.id]) {
      return;
    }
    if (!isSocketOpen()) {
      isElementalsPlanDrawAwaitingFreshState = true;
      return;
    }
    if (isElementalsPlanDrawAwaitingFreshState) {
      return;
    }
    const scopeKey = getPlanScopeKey(state);
    const isAlreadyDrawn = Boolean(elementalsPlanDraw)
      && elementalsPlanDraw.scopeKey === scopeKey && elementalsPlanDraw.playerId === player.id;
    if (isAlreadyDrawn && action.type !== 'LOAD_STATE') {
      return;
    }
    if (!isAlreadyDrawn) {
      elementalsPlanDraw = {
        scopeKey,
        playerId: player.id,
        tokens: addMoreElementalsConstructionTokens([], player.constructionPhaseActions || 0),
      };
    }
    isDrawingElementalsPlanTokens = true;
    try {
      store.dispatch(elementalsSetPlanTokens(
        player.id, elementalsPlanDraw.tokens, selectRound(state), selectPhase(state)
      ));
    } finally {
      isDrawingElementalsPlanTokens = false;
    }
  };

  const showApplyMessage = (turnKey, message) => {
    setTimeout(() => {
      const state = store.getState();
      const player = getConstructionPlanPlayer(state);
      // Ход уже закончился — сообщение о нем неактуально
      if (getConstructionPlanContext(state) !== CONSTRUCTION_PLAN_CONTEXTS.OWN_TURN
        || !player || getConstructionPlanTurnKey(selectRound(state), player.id) !== turnKey
      ) {
        return;
      }
      store.dispatch(showToast(message, 'error', APPLY_MESSAGE_DURATION_MS));
    }, applyMessageDelayMs);
  };

  const runApply = () => {
    // Время хода вышло, сервер вот-вот закроет ход (см. turnInputLock — так
    // бывает, когда связь вернулась на последней секунде хода): строения плана
    // отбросил бы страж блокировки, а сам план считался бы примененным.
    // Поэтому под блокировкой план не применяется. Закроет сервер ход — план
    // снимется вместе с ходом, как любой неуспевший (контекст планирования
    // ниже); снимет блокировку предохранитель — применится в этом же ходе
    if (checkIfTurnInputBlocked(store.getState())) {
      return;
    }
    const result = applyConstructionPlan(store, {
      isSocketOpen,
      ...(checkIfTurnAutoCompleted ? {checkIfTurnAutoCompleted} : {}),
    });
    if (result.status === CONSTRUCTION_PLAN_APPLY_STATUSES.DEFERRED) {
      isAwaitingFreshState = true;
      return;
    }
    if (result.status !== CONSTRUCTION_PLAN_APPLY_STATUSES.APPLIED) {
      return;
    }
    // Чекбокс: повторная проверка — по живому состоянию, как у кнопки
    // завершения фазы: план встал целиком и строения хода расположены верно.
    // Иначе ход остается игроку — с подсветкой невалидного и сообщением, как
    // при попытке завершить ход кнопкой
    const isTurnAutoCompleted = result.isAutoCompleteTurn
      && result.skipped.length === 0
      && validateCurrentPlayerPendingBuildings(store.getState()).isValid;

    // Завершение хода по чекбоксу уходит тем же соединением, что и строения
    // плана, и теряется вместе с ними — повтор нужен и пустому плану. Но
    // только если ход и правда завершался: отказ завершить ход повтор не
    // отменяет — иначе после переподключения ход завершился бы без
    // пропущенного, о котором игроку только что сообщили
    if (result.placed.length > 0 || isTurnAutoCompleted) {
      lastApply = {
        turnKey: result.turnKey,
        entries: result.placed.map(({entry}) => entry),
        isAutoCompleteTurn: isTurnAutoCompleted,
        socket: getSocket(),
        isTouched: false,
        isRetried: Boolean(lastApply && lastApply.turnKey === result.turnKey && lastApply.isRetried),
        isResyncRequested: false,
        scopeKey: getPlanScopeKey(store.getState()),
        // Рамка, в которую перевело завершение хода (последний в фазе — сразу
        // следующая фаза), см. checkIfLastApplyOutlivesScopeChange
        completedScopeKey: null,
      };
    }

    if (result.isAutoCompleteTurn) {
      if (isTurnAutoCompleted) {
        // Оверлея «Ваш ход» не будет: к его показу (EffectsAutoPlayer) ход
        // уже не у игрока. Звук начала хода остается — он подтверждает, что
        // запланированное построено
        playTurnStartSound();
        isCompletingTurn = true;
        try {
          completeTurn(store);
        } finally {
          isCompletingTurn = false;
        }
        return;
      }
      showApplyMessage(result.turnKey, [TEXT_AUTO_COMPLETE_TURN_NOT_DONE, result.message].filter(Boolean).join('\n\n'));
      return;
    }

    if (result.message) {
      showApplyMessage(result.turnKey, result.message);
    }
  };

  /**
   * Чекбокс держится только у валидного плана: после чужой стройки или правки
   * плана самим игроком план проверяется заново. Невалидный — чекбокс
   * снимается, невалидное подсвечивается, игрок получает причины. Без
   * изменений поля и плана проверять нечего (LOAD_STATE с тем же полем)
   */
  const revalidateAutoCompleteTurnPlan = (prevState, nextState) => {
    const state = store.getState();
    const plan = selectConstructionPlanState(state);
    if (!plan.isAutoCompleteTurn) {
      return;
    }
    const isPlanChanged = selectConstructionPlanEntries(prevState) !== plan.entries;
    if (!isPlanChanged && !checkIfFieldChanged(prevState, nextState)) {
      return;
    }

    const validation = validateConstructionPlan(state);
    if (validation.isValid) {
      return;
    }
    store.dispatch(setConstructionPlanAutoCompleteTurn(false));
    store.dispatch(setConstructionPlanInvalidHexKeys(validation.invalidHexKeys));
    store.dispatch(showToast(
      `${TEXT_AUTO_COMPLETE_TURN_DISABLED}${NEWLINE}${NEWLINE}${validation.message}`,
      'warning',
      APPLY_MESSAGE_DURATION_MS,
      undefined,
      true
    ));
  };

  const scheduleApply = () => {
    if (applyTimerId !== null) {
      return;
    }
    applyTimerId = setTimeout(() => {
      applyTimerId = null;
      runApply();
    }, 0);
  };

  const scheduleResync = (applied) => {
    setTimeout(() => {
      // Только пока это тот же ход и то же соединение
      if (lastApply !== applied || getSocket() !== applied.socket
        || getConstructionPlanContext(store.getState()) === CONSTRUCTION_PLAN_CONTEXTS.NONE
      ) {
        return;
      }
      requestState();
    }, resyncDelayMs);
  };

  // Примененного плана в состоянии нет. Чужой ход в той же рамке значит, что
  // сервер не видел и начала своего хода: оно ушло тем же соединением, что и
  // план (ход бота, которого исполняет этот клиент). Свой ход без строений —
  // что не видел действий плана
  const checkIfAppliedPlanMissing = (state) => {
    const context = getConstructionPlanContext(state);
    if (context === CONSTRUCTION_PLAN_CONTEXTS.PLANNING) {
      return true;
    }
    if (context !== CONSTRUCTION_PLAN_CONTEXTS.OWN_TURN) {
      return false;
    }
    const player = getConstructionPlanPlayer(state);
    return getConstructionPlanTurnKey(selectRound(state), player.id) === lastApply.turnKey
      && selectPendingBuildings(state).length === 0;
  };

  /**
   * LOAD_STATE после применения плана, в котором примененного нет (а сам
   * игрок с тех пор ничего не строил):
   *  - по НОВОМУ соединению — действия плана потерялись вместе со старым
   *    (новому сервер присылает LOAD_STATE при подключении, а старое больше
   *    не слушает): план возвращается, один раз, и применится в начале своего
   *    хода — сразу, если он уже идет;
   *  - по ТОМУ ЖЕ соединению — снимок собран раньше, чем сервер обработал
   *    действия плана (ответ на запрос состояния, отказ устаревшему действию
   *    бота-делегата). Сервер их еще обработает, по порядку сообщений сокета,
   *    и повтор построил бы все дважды. Но здесь снимок их уже затер:
   *    состояние запрашивается заново, тоже один раз.
   */
  const handleLoadStateAfterApply = (state) => {
    if (!lastApply || lastApply.isTouched || !checkIfAppliedPlanMissing(state)) {
      return;
    }
    const socket = getSocket();
    if (socket && socket !== lastApply.socket) {
      if (!lastApply.isRetried) {
        lastApply.isRetried = true;
        store.dispatch(restoreConstructionPlan(lastApply.entries, lastApply.isAutoCompleteTurn));
      }
      return;
    }
    if (!lastApply.isResyncRequested) {
      lastApply.isResyncRequested = true;
      scheduleResync(lastApply);
    }
  };

  /**
   * Переживает ли последнее примененное смену рамки плана. Обычно нет: повтор —
   * только в рамках того же хода. Исключение — автоматическое завершение хода
   * последнего в фазе: оно само переводит партию в следующую фазу, а если его
   * действия потерялись с соединением, LOAD_STATE вернет ход обратно. Переход
   * «туда» запоминается, и повтор возможен только при возврате ровно «обратно»
   */
  const checkIfLastApplyOutlivesScopeChange = (prevScopeKey, nextScopeKey) => {
    if (!lastApply) {
      return false;
    }
    if (isCompletingTurn && prevScopeKey === lastApply.scopeKey) {
      lastApply.completedScopeKey = nextScopeKey;
      return true;
    }
    return Boolean(lastApply.completedScopeKey)
      && prevScopeKey === lastApply.completedScopeKey
      && nextScopeKey === lastApply.scopeKey;
  };

  return (next) => (action) => {
    const prevState = store.getState();
    const result = next(action);
    const nextState = store.getState();
    const prevScopeKey = getPlanScopeKey(prevState);
    const nextScopeKey = getPlanScopeKey(nextState);
    // Повтор и отложенное применение — только в рамках того же хода: ключ
    // хода — лишь раунд и игрок, и план прошлой партии иначе мог бы вернуться
    // в новой
    const isScopeChanged = prevScopeKey !== nextScopeKey
      && !checkIfLastApplyOutlivesScopeChange(prevScopeKey, nextScopeKey);

    if (isScopeChanged) {
      lastApply = null;
      isAwaitingFreshState = false;
    }
    if (lastApply && !action._fromServer && LOCAL_CONSTRUCTION_ACTION_TYPES.includes(action.type)) {
      lastApply.isTouched = true;
    }
    if (action.type === 'LOAD_STATE') {
      // Свежее состояние пришло: отложенному применению ждать больше нечего,
      // дальше решает контекст этого состояния
      isAwaitingFreshState = false;
      handleLoadStateAfterApply(nextState);
    }
    ensureElementalsPlanTokens(store.getState(), action);

    const plan = selectConstructionPlanState(store.getState());
    if (plan.entries.length === 0 && plan.brushToken === null && !plan.isAutoCompleteTurn) {
      return result;
    }

    if (isScopeChanged) {
      store.dispatch(clearConstructionPlan());
      return result;
    }

    const context = getConstructionPlanContext(nextState);
    const prevContext = getConstructionPlanContext(prevState);
    if (context === CONSTRUCTION_PLAN_CONTEXTS.NONE) {
      store.dispatch(clearConstructionPlan());
      return result;
    }

    // Начался свой ход: строение, выбранное в плане, остается выбранным и в
    // панели своего хода — иначе выбор игрока слетал бы ровно в момент, когда
    // он наконец может строить. Мост именно здесь: COMPLETE_PHASE уже обнулил
    // selectedToken (редьюсер отработал в next(action)), а кисть плана заберет
    // применение плана (markConstructionPlanApplied) — оно уходит в
    // setTimeout, то есть позже. Панель для этого не годится: она монтируется
    // еще позже, а при открытой инспекции ячейки — только после ее снятия
    if (prevContext === CONSTRUCTION_PLAN_CONTEXTS.PLANNING
      && context === CONSTRUCTION_PLAN_CONTEXTS.OWN_TURN
      && plan.brushToken
    ) {
      store.dispatch(setSelectedToken(plan.brushToken));
    }

    if (prevContext === CONSTRUCTION_PLAN_CONTEXTS.PLANNING) {
      // На занятой ячейке снимается все запланированное; квартал поблизости —
      // только те записи, до которых дотягивается радиус их типа
      const builtOnHexKeys = collectCancelledPlanHexKeys(prevState, nextState, plan.entries);
      const nearQuarterEntries = collectPlanEntriesNearNewQuarters(prevState, nextState, plan.entries)
        .filter(({hexKey}) => !builtOnHexKeys.includes(hexKey));
      // Чужая стройка могла отрезать путь строительства к оставшемуся плану
      // (см. collectUnreachablePlanEntries) — перепроверяем, только если поле
      // вообще поменялось
      const remainingEntries = plan.entries.filter((entry) => (
        !builtOnHexKeys.includes(entry.hexKey) && !nearQuarterEntries.includes(entry)
      ));
      const unreachableEntries = checkIfFieldChanged(prevState, nextState) && remainingEntries.length > 0
        ? collectUnreachablePlanEntries(nextState, remainingEntries)
        : [];
      // Отрезано именно этим экшеном — то, что было достижимо до него. Сироты,
      // которые игрок оставил сам (снял кликом звено в середине цепочки),
      // тоже недостижимы и снимаются, но строящий в этом не виноват. «До»
      // считается по полному плану: снятое на ячейке стройки и у нового
      // квартала до экшена еще стояло и вело к записям за собой
      const previouslyUnreachableEntries = unreachableEntries.length > 0
        ? collectUnreachablePlanEntries(prevState, plan.entries)
        : [];
      const isCutOff = unreachableEntries.some((entry) => !previouslyUnreachableEntries.includes(entry));
      const entriesToRemove = [...nearQuarterEntries, ...unreachableEntries];

      if (builtOnHexKeys.length > 0 || entriesToRemove.length > 0) {
        // План уже не тот, что игрок проверил и доверил ходу. Чекбокс
        // снимается раньше самих записей: иначе снятие записей перепроверило
        // бы план с еще выставленным чекбоксом и дало бы второе сообщение
        if (plan.isAutoCompleteTurn) {
          store.dispatch(setConstructionPlanAutoCompleteTurn(false));
        }
        if (builtOnHexKeys.length > 0) {
          store.dispatch(removeConstructionPlanEntriesAt(builtOnHexKeys));
        }
        if (entriesToRemove.length > 0) {
          store.dispatch(removeConstructionPlanEntries(entriesToRemove));
        }
        // Запланированное снялось не игроком, а чужой стройкой — на слух это
        // отмена его строительства
        playPlanCancelledSound();
        store.dispatch(showToast(
          buildPlanCancelledMessage({
            builderName: getBuilderName(prevState, nextState) || 'Соперник',
            isBuiltOn: builtOnHexKeys.length > 0,
            isNearQuarter: nearQuarterEntries.length > 0,
            isUnreachable: unreachableEntries.length > 0,
            isCutOff,
            isAutoCompleteTurnDisabled: plan.isAutoCompleteTurn,
          }),
          'info',
          7000,
          undefined,
          // Тот же экшен мог передать ход игроку (LOAD_STATE после
          // переподключения) — Toast скрыл бы сообщение по смене ходящего
          true
        ));
      }
    }
    if (context === CONSTRUCTION_PLAN_CONTEXTS.PLANNING) {
      revalidateAutoCompleteTurnPlan(prevState, nextState);
      return result;
    }
    if (isAwaitingFreshState) {
      return result;
    }

    if (getDueConstructionPlanTurnKey(store.getState())) {
      scheduleApply();
    }
    return result;
  };
};

export default createConstructionPlanMiddleware;
